import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export type DocumentType = 'vermittlungsauftrag' | 'besichtigungsschein' | 'kaufanbot';

export interface DocumentRow {
  id: string;
  type: DocumentType;
  /** Migration 0058 — 'allein' (Alleinvermittlungsauftrag, exclusive) or
   *  'einfach' (ordinary, non-exclusive). NULL on the other two types and on
   *  rows written before 0058 that the backfill did not touch. */
  contract_variant: 'allein' | 'einfach' | null;
  number: number;
  client_id: string | null;
  property_id: string | null;
  appointment_id: string | null;
  offer_id: string | null;
  signed: number;
  signed_at: number | null;
  storage_key: string | null;
  pdf_storage_key: string | null;
  /** Batch AI-1 — when set, the deterministic R2 key for the locked
   *  signed-PDF blob. Frontend downloads should fetch this blob instead
   *  of re-rendering from current data. */
  signed_pdf_storage_key: string | null;
  signed_pdf_locked_at: number | null;
  /** JSON-encoded array of ISO codes (always at least `["de"]`). */
  available_languages: string;
  metadata: string | null;
  /** Batch AN — when set, this row is a Gegenanbot child pointing back
   *  at the original Kaufanbot. UI surfaces a ↳ indicator + parent
   *  reference. */
  parent_document_id: string | null;
  signatures_required: number;
  created_by: string | null;
  created_at: number;
}

/**
 * Parse `available_languages` defensively. Old rows pre-Phase-D may have
 * the column missing (rendered as empty/default), and we want a graceful
 * fallback to `['de']` rather than a runtime error.
 */
export function parseAvailableLanguages(raw: string | null | undefined): string[] {
  if (!raw) return ['de'];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const langs = parsed.filter((x): x is string => typeof x === 'string');
      return langs.length > 0 ? langs : ['de'];
    }
  } catch {
    // bad JSON — fall through
  }
  return ['de'];
}

export interface CreateDocumentInput {
  type: DocumentType;
  /** Vermittlungsauftrag only; the server ignores it on the other types and
   *  defaults it to 'allein' when omitted. */
  contractVariant?: 'allein' | 'einfach';
  clientId?: string | null;
  propertyId?: string | null;
  appointmentId?: string | null;
  offerId?: string | null;
  metadata?: Record<string, unknown>;
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDocumentInput) => {
      const res = await apiFetch('/documents', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `document_create_failed_${res.status}`);
      }
      return (await res.json()) as DocumentRow;
    },
    onSuccess: (_, vars) => {
      if (vars.appointmentId)
        qc.invalidateQueries({ queryKey: ['documents', 'appointment', vars.appointmentId] });
      if (vars.propertyId)
        qc.invalidateQueries({ queryKey: ['documents', 'property', vars.propertyId] });
      if (vars.clientId)
        qc.invalidateQueries({ queryKey: ['documents', 'client', vars.clientId] });
    },
  });
}

// Used by the retroactive translation flow (TODO 13.6). Server reconstructs
// the full PDF render input from FK rows so the browser can re-run the
// 10-language orchestrator without a stored render snapshot.
export function useDocumentRenderInput(documentId: string | null) {
  return useQuery({
    queryKey: ['document', documentId, 'render-input'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/documents/${documentId}/render-input`);
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? `render_input_failed_${res.status}`);
      }
      return (await res.json()) as {
        type: 'vermittlungsauftrag' | 'besichtigungsschein' | 'kaufanbot';
        // Shape matches the corresponding *Data interface in pdf/*.tsx.
        // Cast at the call site since the type union is too loose here.
        data: Record<string, unknown>;
      };
    },
    enabled: !!documentId,
    staleTime: 0,
  });
}

export function useDocumentsForAppointment(appointmentId: string | undefined) {
  return useQuery({
    queryKey: ['documents', 'appointment', appointmentId] as const,
    queryFn: async () => {
      const res = await apiFetch(`/documents/by-appointment/${appointmentId}`);
      if (!res.ok) throw new Error(`docs_failed_${res.status}`);
      const json = (await res.json()) as { items: DocumentRow[] };
      return json.items;
    },
    enabled: !!appointmentId,
  });
}

export function useDocumentsForOffer(offerId: string | undefined) {
  return useQuery({
    queryKey: ['documents', 'offer', offerId] as const,
    queryFn: async () => {
      const res = await apiFetch(`/documents/by-offer/${offerId}`);
      if (!res.ok) throw new Error(`docs_failed_${res.status}`);
      const json = (await res.json()) as { items: DocumentRow[] };
      return json.items;
    },
    enabled: !!offerId,
  });
}

export function useDocumentsForProperty(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['documents', 'property', propertyId] as const,
    queryFn: async () => {
      const res = await apiFetch(`/documents/by-property/${propertyId}`);
      if (!res.ok) throw new Error(`docs_failed_${res.status}`);
      const json = (await res.json()) as { items: DocumentRow[] };
      return json.items;
    },
    enabled: !!propertyId,
  });
}

// Plan v2 update 1+2 — upload generated PDF then send to client.
//
// Phase D: pass `lang` to write to the deterministic R2 key
// `documents/{id}/{lang}.pdf` and append the language to the document's
// `available_languages` list. Calls without `lang` keep the legacy
// random-keyed upload path for backwards compat.
export async function uploadDocumentPdf(
  id: string,
  blob: Blob,
  lang?: string,
): Promise<void> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  const res = await apiFetch(`/documents/${id}/pdf${qs}`, {
    method: 'POST',
    body: blob,
    headers: { 'Content-Type': 'application/pdf' },
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(j.message ?? `pdf_upload_failed_${res.status}`);
  }
}

export function useSendDocumentToClient() {
  return useMutation({
    mutationFn: async (input: {
      id: string;
      channel: 'email' | 'sms' | 'whatsapp';
      subject?: string;
      body?: string;
      recipientOverride?: string;
    }) => {
      const res = await apiFetch(`/documents/${input.id}/send-to-client`, {
        method: 'POST',
        body: JSON.stringify({
          channel: input.channel,
          subject: input.subject,
          body: input.body,
          recipientOverride: input.recipientOverride,
        }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? `send_failed_${res.status}`);
      }
      return (await res.json()) as {
        ok: true;
        view_token?: string;
        view_url?: string;
        messageId?: string;
      };
    },
  });
}

export function useCreateMagicLink() {
  return useMutation({
    mutationFn: async ({
      id,
      expiresInMinutes,
    }: {
      id: string;
      expiresInMinutes?: number;
    }) => {
      const res = await apiFetch(`/documents/${id}/magic-link`, {
        method: 'POST',
        body: JSON.stringify({ expiresInMinutes }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? `magic_link_failed_${res.status}`);
      }
      return (await res.json()) as {
        ok: true;
        token: string;
        url: string;
        expires_at: number;
        sms: { status: 'sent' | 'failed' | 'skipped'; error?: string };
      };
    },
  });
}

export async function fetchDocumentSignature(id: string): Promise<string | null> {
  const res = await apiFetch(`/documents/${id}/signature`);
  if (!res.ok) return null;
  const json = (await res.json()) as { ok: boolean; image_base64: string | null };
  return json.image_base64;
}

export function useSignDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, imageBase64 }: { id: string; imageBase64: string }) => {
      const res = await apiFetch(`/documents/${id}/sign-image`, {
        method: 'POST',
        body: JSON.stringify({ imageBase64 }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `sign_failed_${res.status}`);
      }
      return (await res.json()) as { ok: true; storage_key: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useDocumentsForClient(clientId: string | undefined) {
  return useQuery({
    queryKey: ['documents', 'client', clientId] as const,
    queryFn: async () => {
      const res = await apiFetch(`/documents/by-client/${clientId}`);
      if (!res.ok) throw new Error(`docs_failed_${res.status}`);
      const json = (await res.json()) as { items: DocumentRow[] };
      return json.items;
    },
    enabled: !!clientId,
  });
}
